import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  Printer,
  WhatsappLogo,
  DownloadSimple,
  Receipt as ReceiptIcon,
  CreditCard,
  Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { printInvoice, logoAsDataUrl } from "@/lib/printInvoice";

export default function Sales() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [settings, setSettings] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [viewBill, setViewBill] = useState(null);
  const [payBill, setPayBill] = useState(null);
  const [payForm, setPayForm] = useState({
    amount: 0,
    mode: "cash",
    reference: "",
    notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const { data } = await api.get(`/bills?${params.toString()}&limit=500`);
      setBills(data);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      if (r.data?.logo_url) logoAsDataUrl(r.data.logo_url).then(setLogoDataUrl);
    });
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bills;
    return bills.filter(
      (b) =>
        (b.invoice_no || "").toLowerCase().includes(q) ||
        (b.customer_name || "").toLowerCase().includes(q) ||
        (b.customer_mobile || "").toLowerCase().includes(q),
    );
  }, [bills, search]);

  const totals = useMemo(
    () => ({
      count: filtered.length,
      total: filtered.reduce((a, b) => a + (b.grand_total || 0), 0),
      paid: filtered.reduce((a, b) => a + (b.paid || 0), 0),
      balance: filtered.reduce((a, b) => a + (b.balance || 0), 0),
    }),
    [filtered],
  );

  const exportCSV = () => {
    const rows = filtered.map((b) => ({
      invoice_no: b.invoice_no,
      date: (b.created_at || "").slice(0, 10),
      customer: b.customer_name,
      mobile: b.customer_mobile || "",
      sub_total: b.sub_total,
      gst: b.gst_amount,
      grand_total: b.grand_total,
      paid: b.paid,
      balance: b.balance,
    }));
    if (rows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const keys = Object.keys(rows[0]);
    const csv = [keys.join(",")]
      .concat(
        rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(",")),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sales-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printA4 = (b) =>
    printInvoice({ bill: b, settings, mode: "a4", logoDataUrl });
  const printThermal = (b) =>
    printInvoice({ bill: b, settings, mode: "thermal", logoDataUrl });

  const whatsappShare = (b) => {
    const mobile = (b.customer_mobile || "").replace(/[^0-9]/g, "");
    const lines = [
      `*${settings?.store_name || "Invoice"}*`,
      `Invoice: ${b.invoice_no}`,
      `Date: ${(b.created_at || "").slice(0, 10)}`,
      `Customer: ${b.customer_name || "Walk-in"}`,
      ``,
      ...b.items.map((it) => `${it.name} × ${it.quantity}   ${inr(it.amount)}`),
      ``,
      `Subtotal: ${inr(b.sub_total)}`,
      `GST: ${inr(b.gst_amount)}`,
      b.old_gold_value > 0 ? `Less Old Gold: -${inr(b.old_gold_value)}` : "",
      `*Total: ${inr(b.grand_total)}*`,
      `Paid: ${inr(b.paid)}    Balance: ${inr(b.balance)}`,
      ``,
      settings?.invoice_footer || "Thank you!",
    ]
      .filter(Boolean)
      .join("\n");
    const phone = mobile.length > 10 ? mobile : `91${mobile}`;
    const base = mobile ? `https://wa.me/${phone}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(lines)}`, "_blank");
  };

  const openCollect = (b) => {
    setPayBill(b);
    setPayForm({ amount: b.balance, mode: "cash", reference: "", notes: "" });
  };

  const submitPayment = async () => {
    if (!Number(payForm.amount) || Number(payForm.amount) <= 0) {
      toast.error("Amount required");
      return;
    }
    if (paying) return;
    setPaying(true);
    try {
      const { data } = await api.post(`/bills/${payBill.id}/payments`, {
        ...payForm,
        amount: Number(payForm.amount),
      });
      toast.success(`Payment ${inr(payForm.amount)} collected — receipt ready`);
      setBills((arr) => arr.map((b) => (b.id === data.id ? data : b)));
      // Print payment receipt as thermal
      printInvoice({ bill: data, settings, mode: "thermal", logoDataUrl });
      setPayBill(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="p-4 md:p-10 space-y-6" data-testid="sales-page">
      <header className="space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Sales
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            All Bills
          </h1>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[200px] relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              data-testid="sales-search"
              placeholder="Search invoice, customer, mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-card border-border"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              From
            </Label>
            <Input
              data-testid="sales-from"
              type="date"
              className="mt-1 bg-card border-border w-44"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              To
            </Label>
            <Input
              data-testid="sales-to"
              type="date"
              className="mt-1 bg-card border-border w-44"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <Button
            onClick={exportCSV}
            variant="ghost"
            className="text-gold mb-0.5"
          >
            <DownloadSimple size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Bills" value={totals.count} />
        <Stat label="Total" value={inr(totals.total)} accent />
        <Stat label="Paid" value={inr(totals.paid)} />
        <Stat
          label="Outstanding"
          value={inr(totals.balance)}
          bad={totals.balance > 0}
        />
      </section>

      <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
        <Table className="min-w-[820px]">
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Invoice</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-10 text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-10 text-muted-foreground"
                >
                  No bills.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((b) => (
                <TableRow key={b.id} data-testid={`sales-row-${b.id}`}>
                  <TableCell className="font-mono text-xs">
                    {b.invoice_no}
                  </TableCell>
                  <TableCell className="text-sm">
                    {(b.created_at || "").slice(0, 10)}
                  </TableCell>
                  <TableCell>{b.customer_name || "Walk-in"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {b.customer_mobile || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(b.grand_total)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(b.paid)}
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${b.balance > 0 ? "text-gold font-medium" : ""}`}
                  >
                    {inr(b.balance)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-0.5">
                      <button
                        data-testid={`sales-view-${b.id}`}
                        onClick={() => setViewBill(b)}
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                        title="View"
                      >
                        <Eye size={14} />
                      </button>
                      <button
                        data-testid={`sales-print-${b.id}`}
                        onClick={() => printA4(b)}
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                        title="Print A4"
                      >
                        <Printer size={14} />
                      </button>
                      <button
                        data-testid={`sales-whatsapp-${b.id}`}
                        onClick={() => whatsappShare(b)}
                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-green-400"
                        title="WhatsApp"
                      >
                        <WhatsappLogo size={14} />
                      </button>
                      {b.balance > 0 && (
                        <button
                          data-testid={`sales-collect-${b.id}`}
                          onClick={() => openCollect(b)}
                          className="p-1.5 rounded-md hover:bg-accent text-gold"
                          title="Collect payment"
                        >
                          <CreditCard size={14} />
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* View dialog */}
      <Dialog open={!!viewBill} onOpenChange={(v) => !v && setViewBill(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {viewBill?.invoice_no}
            </DialogTitle>
          </DialogHeader>
          {viewBill && (
            <div className="text-sm space-y-3">
              <div className="text-xs text-muted-foreground">
                {new Date(viewBill.created_at).toLocaleString("en-IN")}
              </div>
              <div>
                Customer: <b>{viewBill.customer_name || "Walk-in"}</b>
                {viewBill.customer_mobile
                  ? ` · ${viewBill.customer_mobile}`
                  : ""}
              </div>
              <div className="border-y border-border py-2 space-y-1">
                {viewBill.items.map((it, i) => (
                  <div key={i} className="flex justify-between">
                    <span>
                      {it.name} × {it.quantity}
                    </span>
                    <span className="tabular-nums">{inr(it.amount)}</span>
                  </div>
                ))}
              </div>
              <Row label="Subtotal" value={inr(viewBill.sub_total)} />
              <Row label="GST" value={inr(viewBill.gst_amount)} />
              {viewBill.old_gold_value > 0 && (
                <Row
                  label="Old gold"
                  value={`- ${inr(viewBill.old_gold_value)}`}
                />
              )}
              <div className="flex justify-between pt-2 border-t border-border">
                <b>Total</b>
                <b className="text-gold tabular-nums">
                  {inr(viewBill.grand_total)}
                </b>
              </div>
              <Row label="Paid" value={inr(viewBill.paid)} />
              {viewBill.balance > 0 && (
                <Row label="Balance" value={inr(viewBill.balance)} accent />
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  data-testid="view-print-a4"
                  size="sm"
                  onClick={() => printA4(viewBill)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <Printer size={14} className="mr-1" /> A4
                </Button>
                <Button
                  data-testid="view-print-thermal"
                  size="sm"
                  onClick={() => printThermal(viewBill)}
                  variant="ghost"
                >
                  <Printer size={14} className="mr-1" /> Thermal
                </Button>
                <Button
                  data-testid="view-whatsapp"
                  size="sm"
                  onClick={() => whatsappShare(viewBill)}
                  variant="ghost"
                  className="text-green-400"
                >
                  <WhatsappLogo size={14} className="mr-1" /> WhatsApp
                </Button>
                {viewBill.balance > 0 && (
                  <Button
                    size="sm"
                    onClick={() => {
                      openCollect(viewBill);
                      setViewBill(null);
                    }}
                    className="bg-primary text-primary-foreground"
                  >
                    <CreditCard size={14} className="mr-1" /> Collect
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Collect payment dialog */}
      <Dialog open={!!payBill} onOpenChange={(v) => !v && setPayBill(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Collect Payment
            </DialogTitle>
          </DialogHeader>
          {payBill && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border p-3 text-sm">
                <div className="font-mono text-xs text-muted-foreground">
                  {payBill.invoice_no}
                </div>
                <div>{payBill.customer_name || "Walk-in"}</div>
                <div className="flex justify-between mt-2">
                  <span className="text-muted-foreground">Outstanding</span>
                  <span className="text-gold font-medium tabular-nums">
                    {inr(payBill.balance)}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                    Amount *
                  </Label>
                  <Input
                    data-testid="collect-amount"
                    type="number"
                    step="0.01"
                    className="mt-1.5 bg-background border-border tabular-nums"
                    value={payForm.amount}
                    onChange={(e) =>
                      setPayForm({ ...payForm, amount: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                    Mode
                  </Label>
                  <Select
                    value={payForm.mode}
                    onValueChange={(v) => setPayForm({ ...payForm, mode: v })}
                  >
                    <SelectTrigger className="mt-1.5 bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["cash", "upi", "card", "bank"].map((m) => (
                        <SelectItem key={m} value={m}>
                          {m.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                    Reference (UPI/Cheque #)
                  </Label>
                  <Input
                    className="mt-1.5 bg-background border-border"
                    value={payForm.reference}
                    onChange={(e) =>
                      setPayForm({ ...payForm, reference: e.target.value })
                    }
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                    Notes
                  </Label>
                  <Input
                    className="mt-1.5 bg-background border-border"
                    value={payForm.notes}
                    onChange={(e) =>
                      setPayForm({ ...payForm, notes: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Receipt will auto-print after saving.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayBill(null)}>
              Cancel
            </Button>
            <Button
              data-testid="collect-submit"
              onClick={submitPayment}
              loading={paying}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <CreditCard size={14} className="mr-1" /> Receive & Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, accent, bad }) => (
  <div
    className={`rounded-lg border p-4 ${accent ? "border-gold/40 bg-gold/5" : bad ? "border-rose-500/30 bg-rose-500/5" : "border-border bg-card"}`}
  >
    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </div>
    <div
      className={`font-display text-xl mt-1 tabular-nums ${accent ? "text-gold" : bad ? "text-rose-400" : ""}`}
    >
      {value}
    </div>
  </div>
);

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{label}</span>
    <span className={`tabular-nums ${accent ? "text-gold" : ""}`}>{value}</span>
  </div>
);
